import { useMemo, useState } from 'react';
import type { PublicStoreConfig, StoreProduct } from '../types';
import { useStoreProducts } from '../hooks/useStoreProducts';
import HeroSection from './HeroSection';
import ProductGrid from './ProductGrid';
import StoreFooter from './StoreFooter';

interface Props {
  slug: string;
  config: PublicStoreConfig;
  onAskAbout: (message: string) => void;
}

export function StorePage({ slug, config, onAskAbout }: Props) {
  const storeName = config.name ?? 'Store';
  const storeInitial = storeName.trim().charAt(0).toUpperCase() || 'S';
  const { products, loading } = useStoreProducts(slug);
  const [activeCategory, setActiveCategory] = useState('All');
  const [query, setQuery] = useState('');
  const [logoFailed, setLogoFailed] = useState(false);

  const handleAskAbout = (product: StoreProduct) => {
    onAskAbout(`Tell me more about ${product.name}`);
  };

  const categories = useMemo(
    () => ['All', ...new Set(products.map((product) => product.category).filter(Boolean))] as string[],
    [products],
  );

  const visibleProducts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return products.filter((product) => {
      const matchesCategory = activeCategory === 'All' || product.category === activeCategory;
      const matchesQuery =
        !normalizedQuery ||
        product.name.toLowerCase().includes(normalizedQuery) ||
        product.description.toLowerCase().includes(normalizedQuery) ||
        product.tags.some((tag) => tag.toLowerCase().includes(normalizedQuery));
      return matchesCategory && matchesQuery;
    });
  }, [activeCategory, products, query]);

  return (
    <div className="min-h-screen bg-[#f6f7f9]">
      <header className="sticky top-0 z-40 border-b border-black/5 bg-white/90 px-4 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-3">
          {config.logoUrl && !logoFailed ? (
            <img
              src={config.logoUrl}
              alt={`${storeName} logo`}
              onError={() => setLogoFailed(true)}
              className="h-9 w-9 rounded-xl object-cover ring-1 ring-black/5"
            />
          ) : (
            <span
              className="flex h-9 w-9 items-center justify-center rounded-xl text-sm font-bold text-white"
              style={{ backgroundColor: 'var(--brand-accent)' }}
            >
              {storeInitial}
            </span>
          )}
          <span className="min-w-0 flex-1 truncate text-sm font-bold tracking-tight text-primary sm:text-base">
            {storeName}
          </span>
          <button
            onClick={() => onAskAbout('Help me find the right product')}
            className="inline-flex h-10 items-center gap-2 rounded-full border border-border bg-white px-3.5 text-xs font-semibold text-primary shadow-sm transition-all hover:border-[var(--brand-accent)] hover:text-[var(--brand-accent)] active:scale-[0.98] sm:px-4 sm:text-sm"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            Ask assistant
          </button>
        </div>
      </header>

      <HeroSection
        config={config}
        storeName={storeName}
        storeInitial={storeInitial}
        featuredProducts={products.filter((product) => product.imageUrl).slice(0, 3)}
        onStartChat={() => onAskAbout('Help me find the right product')}
      />

      <main className="mx-auto max-w-6xl px-4 py-10 sm:py-14" id="store-products">
        <div className="mb-7 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--brand-accent)]">The collection</p>
            <h2 className="mt-2 text-2xl font-bold tracking-tight text-primary sm:text-3xl">Browse products</h2>
            <p className="mt-1 text-sm text-mid">
              {loading ? 'Loading the latest items…' : `${products.length} item${products.length === 1 ? '' : 's'} available`}
            </p>
          </div>

          <label className="relative block w-full lg:max-w-xs">
            <span className="sr-only">Search products</span>
            <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 text-mid/60" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search products"
              className="h-11 w-full rounded-full border border-border bg-white pl-10 pr-4 text-sm text-primary shadow-sm outline-none transition focus:border-[var(--brand-accent)] focus:ring-2 focus:ring-[var(--brand-accent)]"
            />
          </label>
        </div>

        {categories.length > 1 && (
          <div className="scroll-smooth-x -mx-4 mb-7 flex gap-2 overflow-x-auto px-4 pb-1" role="tablist" aria-label="Product categories">
            {categories.map((category) => {
              const active = activeCategory === category;
              return (
                <button
                  key={category}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveCategory(category)}
                  className={`shrink-0 rounded-full border px-4 py-2 text-xs font-semibold transition-all active:scale-[0.98] ${
                    active
                      ? 'border-transparent text-white shadow-sm'
                      : 'border-border bg-white text-mid hover:border-[var(--brand-accent)] hover:text-[var(--brand-accent)]'
                  }`}
                  style={active ? { backgroundColor: 'var(--brand-accent)' } : undefined}
                >
                  {category}
                </button>
              );
            })}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="overflow-hidden rounded-[20px] border border-black/5 bg-white">
                <div className="aspect-[4/5] animate-pulse bg-slate-100" />
                <div className="space-y-2 p-4">
                  <div className="h-4 w-3/4 animate-pulse rounded bg-slate-100" />
                  <div className="h-4 w-1/3 animate-pulse rounded bg-slate-100" />
                </div>
              </div>
            ))}
          </div>
        ) : visibleProducts.length > 0 ? (
          <ProductGrid products={visibleProducts} onAskAbout={handleAskAbout} />
        ) : products.length > 0 ? (
          <div className="rounded-[24px] border border-dashed border-border bg-white px-6 py-16 text-center">
            <p className="text-sm font-semibold text-primary">No products match your search</p>
            <button
              onClick={() => {
                setQuery('');
                setActiveCategory('All');
              }}
              className="mt-3 text-sm font-semibold text-[var(--brand-accent)] hover:underline"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="rounded-[24px] border border-dashed border-border bg-white px-6 py-16 text-center">
            <div className="text-3xl opacity-40">📦</div>
            <p className="mt-3 text-sm font-semibold text-primary">No products yet</p>
            <p className="mt-1 text-sm text-mid">Check back soon — this store is just getting started.</p>
          </div>
        )}
      </main>

      <StoreFooter storeName={storeName} />
    </div>
  );
}

export default StorePage;
